import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { cartAdd, dispatchCartAdd } from '@theme/r-cart-actions';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';

/**
 * `<r-pdp-cart-upsell>` — the PDP cross-sell card.
 *
 * Three jobs, all of which have to happen in the browser:
 *
 *   1. VISIBILITY. The card can be filtered against the MAIN product's selected
 *      variant. variant-picker.js morphs only `<variant-picker>` itself, so this
 *      block is never re-rendered by a variant change and the server's decision
 *      would be frozen at first paint. The card's filter is therefore re-run
 *      here on every `variant:update`.
 *
 *   2. PRICE. When the shopper picks a different variant of the UPSELL product,
 *      the button's price has to follow — including the custom-price override,
 *      which is a flat merchant-declared final price rather than a per-variant
 *      one.
 *
 *   3. ADD. Straight through `cartAdd`, then `dispatchCartAdd` so every cart
 *      surface updates and the drawer opens (cart-drawer.js listens for
 *      `CartAddEvent`). The old theme reached into `window.miniCart` directly.
 *
 * The card is a `<r-pdp-cart-upsell>` and NOT a `*-component`, which means the
 * `on:` delegation in component.js will not force-upgrade it. That is fine: the
 * module is a plain `<script type="module">` in the block, so it is registered
 * before the first realistic interaction, and a dropped click would only cost a
 * repeat press rather than a wrong add.
 *
 * @typedef {object} UpsellVariant
 * @property {number} price
 * @property {number} compareAtPrice
 * @property {boolean} available
 * @property {string} title
 *
 * @typedef {object} Refs
 * @property {HTMLSelectElement} [select]
 * @property {HTMLInputElement} variantInput
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} addLabel
 * @property {HTMLElement} [addDot]
 * @property {HTMLElement} [addPrice]
 * @property {HTMLElement} status
 * @property {HTMLTemplateElement} [moneyFormat]
 *
 * @extends {Component<Refs>}
 */
class RPdpCartUpsell extends Component {
  requiredRefs = ['variantInput', 'addButton', 'addLabel'];

  /**
   * How long `[data-added]` stays on the button. Mirrors ADDED_STATE_RESET_DELAY
   * in product-form.js and quick-add.js — the same confirmation should not linger
   * for a different length of time depending on which button you pressed.
   */
  static ADDED_STATE_RESET_DELAY = 800;

  /** How long the button holds its error state before returning to "Add". */
  static ERROR_RESET_MS = 4000;

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resetTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #addedTimer;

  /** @type {Record<string, UpsellVariant>} */
  #variants = {};

  connectedCallback() {
    super.connectedCallback();

    this.#variants = this.#readVariants();
    this.#controller = new AbortController();

    if (this.dataset.filterEnabled === 'true') {
      document.addEventListener(ThemeEvents.variantUpdate, this.#handleMainVariantUpdate, {
        signal: this.#controller.signal,
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = undefined;
    if (this.#resetTimer) clearTimeout(this.#resetTimer);
    if (this.#addedTimer) clearTimeout(this.#addedTimer);
  }

  /**
   * @returns {Record<string, UpsellVariant>} The upsell product's variants, keyed by id.
   */
  #readVariants() {
    const source = this.querySelector('script[data-r-upsell-variants]')?.textContent;
    if (!source) return {};

    try {
      return JSON.parse(source);
    } catch (error) {
      console.warn('[r-pdp-cart-upsell] could not parse variant data', error);
      return {};
    }
  }

  // ── Visibility ────────────────────────────────────────────────────────────

  /**
   * `variant:update` fires on `document` for every product form on the page —
   * a quick-add modal included — so match on productId before reacting.
   *
   * @param {Event} event
   */
  #handleMainVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (!detail) return;

    const eventProductId = detail.data?.productId;
    if (eventProductId != null && this.dataset.productId && String(eventProductId) !== this.dataset.productId) return;

    const variant = detail.resource;
    if (!variant?.id) return;

    this.hidden = !this.#passesFilter(String(variant.id), String(variant.title ?? ''));
  };

  /**
   * Mirrors the Liquid gate in blocks/r-pdp-cart-upsell.liquid exactly: ids and
   * titles are OR'd when both lists are filled, a title matches on equality or
   * containment, and `hide_on` inverts the result.
   *
   * @param {string} variantId
   * @param {string} variantTitle
   * @returns {boolean} Whether the card should be on screen.
   */
  #passesFilter(variantId, variantTitle) {
    const ids = this.#list(this.dataset.filterIds);
    const titles = this.#list(this.dataset.filterTitles).map((title) => title.toLowerCase());

    const title = variantTitle.trim().toLowerCase();
    const idMatches = ids.includes(variantId);
    const titleMatches = titles.some((candidate) => title === candidate || title.includes(candidate));

    let matches;
    if (ids.length && titles.length) matches = idMatches || titleMatches;
    else if (ids.length) matches = idMatches;
    else if (titles.length) matches = titleMatches;
    else matches = true;

    return this.dataset.filterType === 'hide_on' ? !matches : matches;
  }

  /**
   * @param {string | undefined} value - Comma-separated merchant input.
   * @returns {string[]} Trimmed, non-empty entries.
   */
  #list(value) {
    return (value ?? '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  // ── Price ─────────────────────────────────────────────────────────────────

  /**
   * The shopper picked a different variant of the upsell product.
   */
  handleVariantSelect() {
    const { select, variantInput } = this.refs;
    if (!select) return;

    const variant = this.#variants[select.value];
    if (!variant) return;

    variantInput.value = select.value;
    this.#paintButton(variant);
  }

  /**
   * Custom pricing is a flat final price for the whole card, not a per-variant
   * one — the merchant is stating "this promotion costs X", and a discount
   * function makes it true at checkout. So it overrides whatever the selected
   * variant costs, exactly as the Liquid does on first paint.
   *
   * @param {UpsellVariant} variant
   * @returns {{ final: number, compare: number, discounted: boolean }}
   */
  #priceFor(variant) {
    const custom = Number(this.dataset.customPrice ?? 0);

    if (custom > 0) return { final: custom, compare: variant.price, discounted: true };
    if (variant.compareAtPrice > variant.price) {
      return { final: variant.price, compare: variant.compareAtPrice, discounted: true };
    }
    return { final: variant.price, compare: variant.price, discounted: false };
  }

  /**
   * Matches Liquid's `money_without_trailing_zeros`: a whole amount loses its
   * decimal part entirely, a fractional one keeps it. The ISO code the shop's
   * money_format carries is dropped, matching the block's server-rendered
   * markup — the button says "$24", not "$24 CAD".
   *
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    const format = this.refs.moneyFormat?.content.textContent?.trim();
    if (!format) return String(cents);

    const currency = this.dataset.currency ?? '';

    // Strip a zero decimal part only — `(?=\D*$)` keeps the match at the END of
    // the number, so the thousands separator in "$1,000.00" is never eaten.
    return formatMoney(cents, format, currency)
      .replace(/([.,])0+(?=\D*$)/, '')
      .replace(currency, '')
      .trim();
  }

  /**
   * Repaints the button for a variant: label, dot, price, disabled state.
   * @param {UpsellVariant} variant
   */
  #paintButton(variant) {
    const { addButton, addLabel, addDot, addPrice } = this.refs;

    delete addButton.dataset.state;
    addButton.removeAttribute('aria-busy');

    if (!variant.available) {
      addLabel.textContent = this.dataset.labelSoldOut ?? 'Sold out';
      addButton.disabled = true;
      if (addDot) addDot.hidden = true;
      if (addPrice) addPrice.hidden = true;
      return;
    }

    addLabel.textContent = this.dataset.labelAdd ?? 'Add';
    addButton.disabled = false;
    if (addDot) addDot.hidden = false;

    if (addPrice) {
      addPrice.hidden = false;

      // A zero price is the stand-in variant above, not a free product — leave
      // the server's own price markup standing rather than repainting it as $0.
      if (variant.price <= 0) return;

      const { final, compare, discounted } = this.#priceFor(variant);
      addPrice.replaceChildren(
        ...(discounted
          ? [
              this.#priceSpan(compare, 'r-pdp-upsell__price--compare', 's'),
              this.#priceSpan(final, 'r-pdp-upsell__price--sale'),
            ]
          : [this.#priceSpan(final)])
      );
    }
  }

  /**
   * @param {number} cents
   * @param {string} [modifier]
   * @param {string} [tag]
   * @returns {HTMLElement}
   */
  #priceSpan(cents, modifier, tag = 'span') {
    const element = document.createElement(tag);
    element.className = modifier ? `r-pdp-upsell__price ${modifier}` : 'r-pdp-upsell__price';
    element.textContent = this.#money(cents);
    return element;
  }

  // ── Add ───────────────────────────────────────────────────────────────────

  /**
   * The button never rewrites its own label. base.css owns the whole
   * confirmation off `[data-added='true']` — `.add-to-cart-text` clips away and
   * the checkmark burst arrives — so the copy stays "ADD · $22 $14" throughout
   * and the width never changes. Busy is a dim, matching quick-add's variant
   * rows; `aria-disabled` rather than `disabled` keeps the button in the focus
   * order while the request is in flight.
   *
   * @param {Event} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const { addButton, variantInput } = this.refs;
    if (addButton.disabled || addButton.dataset.state === 'busy') return;

    const variantId = variantInput.value;
    if (!variantId) return;

    if (this.#resetTimer) clearTimeout(this.#resetTimer);
    delete addButton.dataset.state;

    addButton.dataset.state = 'busy';
    addButton.setAttribute('aria-busy', 'true');
    addButton.setAttribute('aria-disabled', 'true');

    const result = await cartAdd({ id: variantId, quantity: 1 });

    addButton.removeAttribute('aria-busy');
    addButton.removeAttribute('aria-disabled');
    delete addButton.dataset.state;

    const title = this.dataset.upsellTitle ?? '';

    if (!result.ok) {
      addButton.dataset.state = 'error';
      this.#announce(this.dataset.announceError, title);
      console.warn('[r-pdp-cart-upsell] add failed', result.error);
      this.#resetTimer = setTimeout(() => {
        if (addButton.dataset.state === 'error') delete addButton.dataset.state;
      }, RPdpCartUpsell.ERROR_RESET_MS);
      return;
    }

    this.#announce(this.dataset.announceAdded, title);
    this.#playAddedAnimation();
    this.#flyToCart();

    const cart = await result.cart;
    if (cart) {
      dispatchCartAdd(cart, result.sourceSectionId, {
        source: 'r-pdp-cart-upsell',
        itemCount: cart.item_count,
        variantId,
        sections: result.data?.sections,
      }, this);
    }
  }

  /**
   * The PDP's own added animation, driven exactly as product-form.js and
   * quick-add.js drive it: set the attribute, yield so the style recalc
   * `onAnimationEnd` forces does not land in the same task, then clear it.
   */
  async #playAddedAnimation() {
    const { addButton } = this.refs;

    addButton.dataset.added = 'true';

    await yieldToMainThread();
    await onAnimationEnd(addButton, () => {});

    if (this.#addedTimer) clearTimeout(this.#addedTimer);
    this.#addedTimer = setTimeout(() => {
      delete addButton.dataset.added;
    }, RPdpCartUpsell.ADDED_STATE_RESET_DELAY);
  }

  /**
   * The product image arcing to the cart icon — the other half of the PDP's add,
   * and the half that tells a shopper *where* the thing went.
   *
   * `fly-to-cart.js` is already on every page carrying a product form
   * (snippets/scripts.liquid), so this costs no extra request. It removes itself
   * under reduced motion. The flown image is read off the rendered `<img>`
   * rather than passed down from Liquid, so it is always the picture the shopper
   * is actually looking at — including after a variant swap.
   */
  #flyToCart() {
    const { addButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = /** @type {HTMLImageElement | null} */ (this.querySelector('img.r-pdp-upsell__image'));
    const src = image?.currentSrc || image?.src;

    if (!cartIcon || !src || !customElements.get('fly-to-cart')) return;

    const flyer = /** @type {any} */ (document.createElement('fly-to-cart'));
    flyer.classList.add('fly-to-cart--quick');
    flyer.style.setProperty('background-image', `url(${src})`);
    flyer.style.setProperty('--start-opacity', '0');
    flyer.source = addButton;
    flyer.destination = cartIcon;

    document.body.append(flyer);
  }

  /**
   * @param {string | undefined} template - Copy carrying a `__TITLE__` placeholder.
   * @param {string} title
   */
  #announce(template, title) {
    if (!template) return;
    this.refs.status.textContent = template.replace('__TITLE__', title);
  }
}

if (!customElements.get('r-pdp-cart-upsell')) {
  customElements.define('r-pdp-cart-upsell', RPdpCartUpsell);
}
